// Copyright (c) 2026 aiApas LLC. All Rights Reserved.
// GuardRok — Proprietary and Confidential. Patent Pending.
// Unauthorized use or distribution is prohibited.

import { useState, useRef, useEffect } from 'react'
import { X, Send, Loader2 } from 'lucide-react'
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../lib/config'
import { GuardRokIcon } from './GuardRokLogo'

const EDGE_FN_URL = `${SUPABASE_URL}/functions/v1/website-qualifier-chat`
const BELL_URL = `${SUPABASE_URL}/functions/v1/send-notification-email`

function linkify(text: string): string {
  const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return escaped
    .replace(
      /(https?:\/\/[^\s]+)/g,
      '<a href="$1" target="_blank" rel="noopener noreferrer" style="color:#fca5a5;text-decoration:underline;word-break:break-all">$1</a>'
    )
    .replace(
      /\/(demo|try|signup|pricing)(?=[\s,.]|$)/g,
      '<a href="/$1" target="_blank" style="color:#fca5a5;text-decoration:underline">/$1</a>'
    )
}

type Role = 'agent' | 'visitor'
interface Message {
  role: Role
  text: string
}

const GREETING: Message = {
  role: 'agent',
  text: "Hi — I'm Roki, GuardRok's assistant. What brings you here today — looking to evaluate, just researching, or something specific?",
}

// Extract the first email address found in conversation messages
function extractEmail(msgs: Message[]): string {
  const emailRe = /[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/
  for (const m of msgs) {
    const match = m.text.match(emailRe)
    if (match) return match[0]
  }
  return ''
}

// Fire-and-forget notification to hello@guardrok.com via BELL
async function notifyTeam(msgs: Message[], trigger: string, page: string) {
  try {
    const visitorEmail = extractEmail(msgs)
    const transcript = msgs
      .map(m => `${m.role === 'visitor' ? 'Visitor' : 'Roki'}: ${m.text}`)
      .join('\n')

    await fetch(BELL_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        apikey: SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        type: 'generic',
        title: `ROKI lead — ${trigger} — ${page}`,
        notification_body: `Visitor email: ${visitorEmail || 'not captured'}\nPage: ${page}\nTrigger: ${trigger}\n\n--- Transcript ---\n${transcript}`,
        metadata: { email: visitorEmail, page, trigger },
      }),
    })
  } catch {
    /* silent — never interrupt the chat */
  }
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([GREETING])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const notifiedRef = useRef(false) // fire BELL once per session

  useEffect(() => {
    if (open) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [open, messages])

  async function send() {
    const text = input.trim()
    if (!text || loading) return
    setInput('')

    const userMsg: Message = { role: 'visitor', text }
    const nextMessages = [...messages, userMsg]
    setMessages(nextMessages)
    setLoading(true)

    try {
      const history = messages.map(m => ({ role: m.role, text: m.text }))
      const res = await fetch(EDGE_FN_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          apikey: SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          message: text,
          history,
          visitor_metadata: {
            page: window.location.pathname,
            country: 'unknown',
          },
        }),
      })

      const data = await res.json()
      const replyText =
        data.reply_text || 'Sorry, I had a technical issue. Email us at hello@guardrok.com.'
      const nextAction = data.next_action as string | undefined
      const qualified = data.qualified as boolean | undefined

      const updatedMsgs = [...nextMessages, { role: 'agent' as Role, text: replyText }]
      setMessages(updatedMsgs)

      // Notify the team once when visitor is qualified or needs a human
      if (
        !notifiedRef.current &&
        (qualified === true || nextAction === 'hand_to_human' || nextAction === 'book_demo')
      ) {
        notifiedRef.current = true
        const trigger =
          nextAction === 'hand_to_human'
            ? 'hand_to_human'
            : qualified
              ? 'qualified_lead'
              : (nextAction ?? 'unknown')
        notifyTeam(updatedMsgs, trigger, window.location.pathname)
      }
    } catch {
      setMessages(prev => [
        ...prev,
        { role: 'agent', text: 'Sorry, something went wrong. Email us at hello@guardrok.com.' },
      ])
    }

    setLoading(false)
  }

  function handleKey(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send()
    }
  }

  return (
    <>
      {/* Chat panel */}
      {open && (
        <div
          style={{
            position: 'fixed',
            bottom: 88,
            right: 24,
            zIndex: 9999,
            width: 360,
            maxHeight: 520,
            background: '#111827',
            border: '1px solid #1f2937',
            borderRadius: 16,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '14px 16px',
              borderBottom: '1px solid #1f2937',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: 32,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <GuardRokIcon size={28} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 14, lineHeight: 1 }}>
                Roki
              </div>
              <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>
                GuardRok's assistant
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
            >
              <X size={18} color="#6b7280" />
            </button>
          </div>

          {/* Messages */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px 12px',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {messages.map((m, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  justifyContent: m.role === 'visitor' ? 'flex-end' : 'flex-start',
                }}
              >
                <div
                  style={{
                    maxWidth: '82%',
                    padding: '8px 12px',
                    borderRadius:
                      m.role === 'visitor' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                    background: m.role === 'visitor' ? '#E63946' : '#1f2937',
                    color: '#fff',
                    fontSize: 13,
                    lineHeight: 1.5,
                  }}
                >
                  {m.role === 'agent' ? (
                    <span dangerouslySetInnerHTML={{ __html: linkify(m.text) }} />
                  ) : (
                    m.text
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div
                  style={{
                    padding: '8px 14px',
                    borderRadius: '12px 12px 12px 2px',
                    background: '#1f2937',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <Loader2
                    size={13}
                    color="#6b7280"
                    style={{ animation: 'spin 1s linear infinite' }}
                  />
                  <span style={{ color: '#6b7280', fontSize: 12 }}>Typing…</span>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div
            style={{
              padding: '10px 12px',
              borderTop: '1px solid #1f2937',
              display: 'flex',
              gap: 8,
              flexShrink: 0,
            }}
          >
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Type a message…"
              disabled={loading}
              style={{
                flex: 1,
                background: '#030712',
                border: '1px solid #374151',
                borderRadius: 10,
                padding: '8px 12px',
                color: '#fff',
                fontSize: 13,
                outline: 'none',
              }}
            />
            <button
              onClick={send}
              disabled={!input.trim() || loading}
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                flexShrink: 0,
                background: input.trim() && !loading ? '#E63946' : '#374151',
                border: 'none',
                cursor: input.trim() && !loading ? 'pointer' : 'default',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.15s',
              }}
            >
              <Send size={15} color="#fff" />
            </button>
          </div>
        </div>
      )}

      {/* Bubble button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 9999,
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: '#E63946',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 20px rgba(230,57,70,0.5)',
          transition: 'transform 0.15s, box-shadow 0.15s',
        }}
        onMouseEnter={e => {
          ;(e.currentTarget as HTMLElement).style.transform = 'scale(1.08)'
        }}
        onMouseLeave={e => {
          ;(e.currentTarget as HTMLElement).style.transform = 'scale(1)'
        }}
        aria-label={open ? 'Close chat' : 'Open chat'}
      >
        {open ? <X size={22} color="#fff" /> : <GuardRokIcon size={28} />}
      </button>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </>
  )
}
